import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Copy } from 'lucide-react';

import useAdminCategories from '../../hooks/useAdminCategories';
import { fetchAdminProduct, createProduct, updateProduct } from '../../hooks/useAdminProducts';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useSpecialCharPicker } from '../../components/ui/SpecialCharDialog';
import PhotoManager from '../../components/admin/PhotoManager';
import CategoryParentPicker from '../../components/admin/CategoryParentPicker';
import { parseApiError } from '../../utils/apiError';

const dollarsToCents = (dollars) => {
  const n = parseFloat(dollars);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};
const centsToDollars = (cents) => (Number.isFinite(cents) ? (cents / 100).toFixed(2) : '');

export default function ProductEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { categories, loading: categoriesLoading } = useAdminCategories();

  const [product, setProduct] = useState(null); // full doc in edit mode (for photos)
  const [values, setValues] = useState({ title: '', description: '', category: '', price: '', active: true });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isNew) return undefined;
    let active = true;
    setLoading(true);
    fetchAdminProduct(id)
      .then((p) => {
        if (!active) return;
        setProduct(p);
        setValues({
          title: p.title || '',
          description: p.description || '',
          category: p.category?._id || p.category || '',
          price: centsToDollars(p.price),
          active: p.active !== false,
        });
      })
      .catch(() => {
        if (active) addToast('Could not load product', { variant: 'error' });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, isNew, addToast]);

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  // Last known caret position in the title field, so the special-character
  // button (which moves focus into its own dialog) still knows where to
  // insert — the input's own selectionStart can't be trusted once focus
  // has left it. See CategoryForm.js for the same pattern.
  const titleCursorRef = useRef(null);
  const trackTitleCursor = (e) => {
    titleCursorRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
  };
  const { openPicker, dialog: specialCharDialog } = useSpecialCharPicker();

  const insertIntoTitle = (char) => {
    setValues((v) => {
      const text = v.title || '';
      const { start = text.length, end = text.length } = titleCursorRef.current || {};
      const nextPos = start + char.length;
      titleCursorRef.current = { start: nextPos, end: nextPos };
      return { ...v, title: text.slice(0, start) + char + text.slice(end) };
    });
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/products/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      addToast('Link copied!', { variant: 'success' });
    } catch {
      addToast('Could not copy link', { variant: 'error' });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const cents = dollarsToCents(values.price);
    if (!Number.isFinite(cents) || cents < 0) {
      setFieldErrors({ price: 'Enter a valid price (e.g. 12.99)' });
      return;
    }

    const body = {
      title: values.title,
      description: values.description,
      category: values.category,
      price: cents,
      active: values.active,
    };

    setSaving(true);
    try {
      if (isNew) {
        const created = await createProduct(body);
        addToast('Product created — you can now add photos', { variant: 'success' });
        navigate(`/admin/products/${created._id}`, { replace: true });
      } else {
        const updated = await updateProduct(id, body);
        setProduct(updated);
        addToast('Product saved', { variant: 'success' });
      }
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
    } finally {
      setSaving(false);
    }
  };

  if (loading || categoriesLoading) {
    return <div className="ss-state"><Spinner label="Loading product" size={32} /></div>;
  }

  return (
    <div>
      <p style={{ marginBottom: 'var(--ss-space-3)' }}>
        <Link to="/admin/products" className="ss-back-link"><ArrowLeft size={16} aria-hidden="true" /> Back to Products</Link>
      </p>
      <h1>{isNew ? 'New product' : 'Edit product'}</h1>
      {!isNew && (
        <div style={{ marginTop: 'var(--ss-space-2)', display: 'flex', alignItems: 'center', gap: 'var(--ss-space-2)' }}>
          <span className="ss-label">Product Link</span>
          <button
            type="button"
            className="ss-icon-btn"
            onClick={copyLink}
            title="Copy product link"
            aria-label="Copy product link"
          >
            <Copy size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="ss-admin-editor" style={{ marginTop: 'var(--ss-space-5)' }}>
        <Card>
          <form className="ss-form" onSubmit={save} noValidate>
            {formError && <p className="ss-form__error" role="alert">{formError}</p>}

            <Input
              label="Title"
              value={values.title}
              onChange={(e) => {
                setField('title')(e);
                trackTitleCursor(e);
              }}
              onSelect={trackTitleCursor}
              error={fieldErrors.title}
              required
              endAdornment={
                <button
                  type="button"
                  className="ss-input-reveal"
                  title="Insert special character"
                  aria-label="Insert special character"
                  // Prevents the browser's default mousedown focus-steal so the
                  // title field's caret position isn't lost when this is clicked.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => openPicker(insertIntoTitle)}
                >
                  &#937;
                </button>
              }
            />

            <div className="ss-field">
              <label htmlFor="prod-desc" className="ss-label">Description</label>
              <textarea
                id="prod-desc"
                className={`ss-input ${fieldErrors.description ? 'ss-input--error' : ''}`.trim()}
                rows={5}
                value={values.description}
                onChange={setField('description')}
              />
              {fieldErrors.description && <p className="ss-field__error">{fieldErrors.description}</p>}
            </div>

            <div className="ss-field">
              <span className="ss-label">Category</span>
              <CategoryParentPicker
                categories={categories}
                value={values.category || null}
                onChange={(category) => setValues((v) => ({ ...v, category: category || '' }))}
                leafOnly
              />
              {fieldErrors.category && <p className="ss-field__error">{fieldErrors.category}</p>}
            </div>

            <div className="ss-form__row">
              <Input
                label="Price (USD)"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={values.price}
                onChange={setField('price')}
                error={fieldErrors.price}
                required
              />
            </div>

            <label className="ss-check">
              <input
                type="checkbox"
                checked={values.active}
                onChange={(e) => setValues((v) => ({ ...v, active: e.target.checked }))}
              />
              <span>Active (visible in the storefront)</span>
            </label>

            <div className="ss-form__actions">
              <Button type="submit" disabled={saving}>
                {saving ? <Spinner label="Saving" /> : 'Save product'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate('/admin/products')}>Cancel</Button>
            </div>
            {specialCharDialog}
          </form>
        </Card>

        <Card>
          <h2 style={{ fontSize: '1.25rem', marginBottom: 'var(--ss-space-3)' }}>Photos</h2>
          {isNew ? (
            <p className="ss-admin__lede">Save the product first, then add photos here.</p>
          ) : (
            <PhotoManager
              productId={product?._id || id}
              photos={product?.photos || []}
              onChanged={(updated) => setProduct(updated)}
            />
          )}
        </Card>
      </div>
    </div>
  );
}
